/**
 * Shapes returned by the Green Candle API.
 *
 * Money and quantities arrive as strings, never as numbers. The backend stores everything as
 * NUMERIC(38,18) and serialises it in plain notation (D-07); parsing that into a JavaScript
 * number would silently round it, which is exactly the class of error the backend goes to
 * some length to avoid. Format them for display, and do arithmetic on the server.
 */
export type Decimal = string;

/**
 * A figure the server could not produce.
 *
 * The backend serialises with non-null inclusion, so such a field is **absent** from the JSON
 * rather than present as null - it reaches the client as `undefined`. The declarations below
 * say `| null` because that is what the field means; compare with `== null`, never `=== null`,
 * or every unknown reads as a value.
 */

export type AssetClass = 'STOCK' | 'ETF' | 'OPTION' | 'CRYPTO' | 'CASH' | 'CUSTOM';

export type AllocationDimension = 'ASSET_CLASS' | 'SECTOR' | 'CURRENCY' | 'ACCOUNT';

export type AccountKind = 'BROKER' | 'EXCHANGE' | 'MANUAL';

export type CostBasisMethod = 'FIFO' | 'LIFO' | 'AVERAGE' | 'SPECIFIC_ID';

export type SnapshotSource = 'SCHEDULED' | 'BACKFILL' | 'MANUAL';

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresInSeconds: number;
}

export interface Me {
  id: string;
  username: string;
  email: string;
  lastLoginAt: string | null;
  features: FeatureFlags;
}

/**
 * What this installation has switched on. Every flag defaults to off on the server, so a client
 * that cannot read them should show nothing rather than everything.
 *
 * <p>Already resolved: a sub-flag such as `briefingEvents` is false when the briefing itself is
 * off, so nothing here has to be read together with anything else.
 */
export interface FeatureFlags {
  wizard: boolean;
  quant: boolean;
  options: boolean;
  maintenance: boolean;
  finnhub: boolean;
  briefing: boolean;
  briefingEvents: boolean;
  briefingScenarios: boolean;
  /** UI-only: these hide a terminal tab and close no endpoint. */
  viewCrypto: boolean;
  viewCashflow: boolean;
  viewAssets: boolean;
}

/** Everything off - what to assume when the flags are not known yet. */
export const NO_FEATURES: FeatureFlags = {
  wizard: false,
  quant: false,
  options: false,
  maintenance: false,
  finnhub: false,
  briefing: false,
  briefingEvents: false,
  briefingScenarios: false,
  viewCrypto: false,
  viewCashflow: false,
  viewAssets: false,
};

export interface Account {
  id: string;
  name: string;
  kind: AccountKind;
  baseCurrency: string;
  costBasisMethod: CostBasisMethod;
  externalId: string | null;
}

/**
 * Every derived figure is nullable, and a null means "not known" rather than zero. `priceNote`
 * and `fxNote` say why when one is missing, and the UI is expected to show that rather than
 * rendering a dash and leaving the user to guess (NFR-U4).
 */
export interface Holding {
  instrumentId: string;
  symbol: string;
  /** The company for an asset; the contract, as IBKR writes it, for an option. */
  name: string;
  assetClass: AssetClass;
  quantity: Decimal;
  averageCost: Decimal;
  costBasis: Decimal;
  currency: string;
  openLotCount: number;
  baseCurrency: string;
  costBasisInBase: Decimal | null;
  fxRate: string | null;
  fxSource: string | null;
  fxNote: string | null;
  lastPrice: Decimal | null;
  priceDate: string | null;
  priceSource: string | null;
  marketValue: Decimal | null;
  unrealisedPnl: Decimal | null;
  unrealisedPnlPercent: Decimal | null;
  marketValueInBase: Decimal | null;
  weightPercent: Decimal | null;
  priceNote: string | null;
  /** Present only when assetClass is OPTION. */
  option: OptionContract | null;
}

/**
 * The contract behind an option holding, as the server reads it.
 *
 * <p>Everything here could be recovered from `symbol` — `AVGO 19FEB27 360 C` carries all of
 * it — but that string is for display. Parsing it here would be a second implementation of a
 * grammar the importer already owns, and the two would disagree the first time a symbol
 * turns up that neither has seen.
 */
export interface OptionContract {
  underlyingSymbol: string;
  strike: Decimal;
  right: 'CALL' | 'PUT';
  expiry: string;
  multiplier: Decimal;
  /** Negative once expiry has passed. */
  daysToExpiry: number;
  expired: boolean;
}

/** FR-E2. One day of the equity curve. */
export interface Snapshot {
  date: string;
  baseCurrency: string;
  marketValue: Decimal | null;
  costBasis: Decimal | null;
  unrealisedPnl: Decimal | null;
  unrealisedPnlPercent: Decimal | null;
  holdingsTotal: number;
  holdingsValued: number;
  complete: boolean;
  source: SnapshotSource;
  note: string | null;
}

export interface Gap {
  from: string;
  to: string;
  days: number;
}

export interface GapReport {
  from: string | null;
  to: string | null;
  expectedDays: number;
  presentDays: number;
  missingDays: number;
  complete: boolean;
  gaps: Gap[];
}

export interface BackfillOutcome {
  from: string | null;
  to: string | null;
  daysWritten: number;
  daysIncomplete: number;
  detail: string;
}

export interface AllocationBucket {
  key: string;
  value: Decimal;
  holdings: number;
  weightPercent: Decimal | null;
}

export interface UnvaluedHolding {
  symbol: string;
  account: string;
  bucket: string;
  reason: string;
}

/** FR-E4. One breakdown, its denominator, and everything left out of it. */
export interface AllocationReport {
  dimension: AllocationDimension;
  currency: string;
  asOf: string;
  total: Decimal;
  holdingsConsidered: number;
  holdingsValued: number;
  buckets: AllocationBucket[];
  unvalued: UnvaluedHolding[];
  note: string | null;
}

export interface Instrument {
  id: string;
  assetClass: AssetClass;
  symbol: string;
  name: string;
  currency: string;
  exchange: string | null;
  isin: string | null;
  sector: string | null;
  baseAsset: string | null;
  quoteAsset: string | null;
}

/** AST-1. Just the fields a CUSTOM asset (property, a private loan) actually needs. */
export interface CreateCustomInstrumentRequest {
  assetClass: 'CUSTOM';
  symbol: string;
  name: string;
  currency: string;
}

export interface Page<T> {
  items: T[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

/** RFC 7807, which is what the backend returns for every error (Phase 00 API conventions). */
export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
}

// ------------------------------------------------------- FR-B1/B2: IBKR statement import

export interface StatementHeader {
  accountId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  baseCurrency: string | null;
  currencies: string[];
}

/**
 * The line to read first. `importable` is false whenever reconciliation fails or a row could
 * not be read - D-04 makes that a block rather than a warning, because a blocked import is
 * recoverable and a wrong cost basis propagates into every figure the system will ever show.
 */
export interface ImportVerdict {
  importable: boolean;
  headline: string;
  reasons: string[];
}

export interface ReconciliationLine {
  label: string;
  statement: Decimal;
  parsed: Decimal;
  difference: Decimal;
  balanced: boolean;
}

export interface ReconciliationCurrency {
  currency: string;
  balanced: boolean;
  /** Whether the statement's own cash arithmetic closes - i.e. whether the oracle was read. */
  internallyConsistent: boolean;
  startingCash: Decimal;
  endingCash: Decimal;
  impliedEndingCash: Decimal;
  lines: ReconciliationLine[];
}

export interface ReconciliationReport {
  balanced: boolean;
  summary: string;
  currencies: ReconciliationCurrency[];
}

export interface ImportTotals {
  tradeCount: number;
  buyCount: number;
  sellCount: number;
  cashFlowCount: number;
  instrumentCount: number;
  positionCount: number;
  unparsedCount: number;
  netCashByCurrency: Record<string, Decimal>;
}

export interface ParsedTrade {
  symbol: string;
  assetCategory: string;
  currency: string;
  executedAt: string;
  side: 'BUY' | 'SELL';
  quantity: Decimal;
  price: Decimal;
  proceeds: Decimal;
  commission: Decimal;
  realizedPnl: Decimal;
  codes: string | null;
}

export interface ParsedCashFlow {
  type: string;
  date: string;
  currency: string;
  symbol: string | null;
  description: string;
  amount: Decimal;
}

export interface ParsedInstrumentInfo {
  symbol: string;
  description: string;
  assetCategory: string;
  securityId: string;
  exchange: string | null;
  multiplier: Decimal;
}

export interface ParsedPosition {
  symbol: string;
  currency: string;
  quantity: Decimal;
  costBasis: Decimal;
  closePrice: Decimal;
  value: Decimal;
}

/** FR-B4. A row inside a recognised section that could not be read, with its raw text. */
export interface UnparsedRow {
  section: string;
  page: number;
  rawLine: string;
  reason: string;
}

export interface StatementReport {
  header: StatementHeader;
  verdict: ImportVerdict;
  reconciliation: ReconciliationReport;
  totals: ImportTotals;
  trades: ParsedTrade[];
  cashFlows: ParsedCashFlow[];
  instruments: ParsedInstrumentInfo[];
  positions: ParsedPosition[];
  unparsed: UnparsedRow[];
}

/** A stored report as it appears in the list of past imports. */
export interface SavedStatementSummary {
  id: string;
  filename: string;
  fileSize: number;
  accountId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  baseCurrency: string | null;
  importable: boolean;
  reconciled: boolean;
  tradeCount: number;
  cashFlowCount: number;
  unparsedCount: number;
  headline: string;
  createdAt: string;
}

/**
 * `alreadyExisted` is true when this exact file had been read before and the stored report was
 * returned rather than a new one created.
 */
export interface SavedStatementResponse {
  summary: SavedStatementSummary;
  report: StatementReport;
  alreadyExisted: boolean;
}

/** FR-C4. One acquisition, as the holdings table expands it. */
export interface Lot {
  id: string;
  instrumentId: string;
  originalQuantity: Decimal;
  openQuantity: Decimal;
  costPerUnit: Decimal;
  currency: string;
  openedOn: string;
  closedOn: string | null;
  isLong: boolean;
}

/** FR-E3. Time-weighted return over a range, with money in and out removed. */
export interface TimeWeightedReturn {
  from: string;
  to: string;
  currency: string;
  twrPercent: Decimal | null;
  /** Null under 30 days: annualising a short run turns noise into a headline. */
  annualisedPercent: Decimal | null;
  startingValue: Decimal | null;
  endingValue: Decimal | null;
  netFlow: Decimal;
  periods: number;
  skipped: number;
  note: string | null;
}

/** FR-B2/B3. What committing a statement did, or would do. */
export interface CommitOutcome {
  accountId: string;
  dryRun: boolean;
  tradesConsidered: number;
  tradesCreated: number;
  tradesAlreadyPresent: number;
  cashFlowsCreated: number;
  cashFlowsAlreadyPresent: number;
  instrumentsCreated: number;
  warnings: string[];
}

/**
 * FR-E7. What relinking imported cash flows did.
 *
 * <p>`examined` counts only the rows that could be repaired at all: a cash row whose statement
 * named a ticker and whose transaction still points at no instrument. `unresolved` are the ones
 * naming something this account has never held - the statement that bought it has not been
 * imported - and they stay unattributed rather than having an instrument invented for them.
 */
/** A11. What a backfill of stored statements wrote, or would write on a dry run. */
export interface BalanceBackfill {
  reportsFound: number;
  reportsApplied: number;
  cashBalancesRecorded: number;
  pricesSeeded: number;
  /** Reports left alone, each with the reason - an unknown account, or one that never reconciled. */
  skipped: string[];
}

export interface CashFlowRelink {
  examined: number;
  linked: number;
  unresolved: number;
}

/**
 * FR-B11. What moving imported fills onto the form-independent identity did, or would do.
 *
 * <p>{@code orphaned} counts imported fills no stored statement accounts for. It should be zero;
 * anything else means a statement was deleted after its commit or a fill is stored twice, and no
 * CSV should be imported until that is understood.
 */
export interface TradeRehash {
  reportsFound: number;
  upgraded: number;
  alreadyCurrent: number;
  orphaned: number;
  /** Stored reports that could not be read, each with the reason. */
  skipped: string[];
}

export type TransactionKind =
  | 'BUY' | 'SELL' | 'DIVIDEND' | 'INTEREST' | 'FEE' | 'TAX_WITHHOLDING'
  | 'FX' | 'TRANSFER' | 'SPLIT'
  | 'OPTION_EXERCISE' | 'OPTION_ASSIGNMENT' | 'OPTION_EXPIRY' | 'STAKING_REWARD'
  | 'RENTAL_INCOME';

/** FR-H4. One transaction as the list shows it. */
export interface TransactionRow {
  id: string;
  type: TransactionKind;
  tradeDate: string;
  instrumentId: string | null;
  symbol: string | null;
  assetClass: AssetClass | null;
  quantity: Decimal | null;
  unitPrice: Decimal | null;
  fees: Decimal | null;
  amount: Decimal | null;
  currency: string;
  source: string | null;
  /**
   * The broker's own codes on the imported row: `O` opened this position, `C` closed it, and
   * combinations like `C;P` for a partial close. Null for a trade entered by hand, which has no
   * broker to have an opinion about it.
   */
  brokerCodes: string | null;
  /**
   * Whether this row may be edited or deleted. Decided by the server, not re-derived here: the
   * rule (manual rows only, and never a dividend) is enforced by ManualEntryService, and a
   * screen that worked it out independently would drift from it.
   */
  editable: boolean;
  /** Why not, for the reader, or null when it is editable. */
  notEditableBecause: string | null;
  note: string | null;
}

/**
 * FR-B5. A transaction entered or corrected by hand.
 *
 * Which fields matter depends on the type: BUY and SELL need an instrument, a quantity and a
 * price; everything else needs an amount. The server enforces that and names the missing field,
 * rather than the shape of this type trying to express it.
 */
export interface ManualEntry {
  type: TransactionKind;
  tradeDate: string;
  instrumentId?: string | null;
  quantity?: Decimal | null;
  unitPrice?: Decimal | null;
  fees?: Decimal | null;
  amount?: Decimal | null;
  currency?: string | null;
  note?: string | null;
}

/**
 * FR-E5. One day of the growth comparison, both sides indexed to 100 on the first day.
 *
 * `benchmark` is null on a day the benchmark did not trade — a market holiday the account
 * still has a snapshot for. Left empty rather than carried forward, so a chart draws a break
 * instead of inventing a flat day.
 */
export interface BenchmarkPoint {
  date: string;
  portfolio: Decimal;
  benchmark: Decimal | null;
}

export interface BenchmarkComparison {
  symbol: string;
  name: string | null;
  from: string | null;
  to: string | null;
  /** Time-weighted, so deposits are not counted as performance. */
  portfolioReturnPercent: Decimal | null;
  benchmarkReturnPercent: Decimal | null;
  series: BenchmarkPoint[];
  note: string | null;
}

/**
 * FR-D2. A live-ish price for one instrument.
 *
 * `delayMinutes` comes from the provider rather than being assumed here: 15 for a free
 * equity feed, 0 for a crypto exchange. A screen that has the number can label the price
 * honestly instead of implying a delayed price is current.
 */
export interface Quote {
  instrumentId: string;
  symbol: string;
  price: Decimal;
  currency: string;
  /** ISO instant: when the provider says this price was true, not when it was fetched. */
  asOf: string;
  source: string;
  delayMinutes: number;
}

// --------------------------------------------------------- FR-H11: options strategy payoff

export type StrategyKind =
  | 'COLLAR' | 'COVERED_CALL' | 'PROTECTIVE_PUT' | 'IRON_CONDOR' | 'BUTTERFLY'
  | 'VERTICAL_SPREAD' | 'STRADDLE' | 'STRANGLE' | 'SINGLE_LEG' | 'CUSTOM';

/** One leg of an option strategy, as the payoff engine reads it. */
export interface OptionLeg {
  /**
   * The leg's own instrument - the canonical OCC symbol the position is stored under.
   *
   * A leg can be split across two strategies (a stock position covering 1 of 2 written calls
   * claims half a leg), so the same symbol can appear twice with different quantities. Group
   * holdings by strategy with this, never assume one symbol belongs to one strategy.
   */
  symbol: string;
  /** Signed contracts: negative is a written (short) leg. */
  quantity: Decimal;
  strike: Decimal;
  right: 'CALL' | 'PUT';
  multiplier: Decimal;
  /** Multiplier-scaled and signed: positive for a long (debit) leg, negative for a short
   *  (credit) one - the same convention `Holding.costBasis` uses. */
  costBasis: Decimal;
}

/** One point the payoff at expiration is known exactly at. */
export interface PayoffPoint {
  price: Decimal;
  pnl: Decimal;
}

/**
 * The underlying's price to anchor a strategy card against - 15-minute-delayed and labelled as
 * such when it is a live quote. `delayMinutes` is null when this is a stored close instead: a
 * fact about the whole of `asOf`'s day, not a delay off the current moment.
 */
export interface OptionSpot {
  price: Decimal;
  asOf: string;
  source: string;
  delayMinutes: number | null;
}

/**
 * FR-H11. One strategy auto-detected from the account's option positions (collar, covered
 * call, iron condor, ...; unmatched legs group into `CUSTOM`), with its payoff at expiration
 * worked out exactly - no option prices needed, since D-14 stays closed. `spot` is null
 * exactly when `spotNote` explains why; `maxProfit`/`maxLoss` are null exactly when their
 * `*Unbounded` flag is true, and render as "unlimited", never as a number or a dash.
 */
export interface OptionStrategy {
  underlying: string;
  kind: StrategyKind;
  /** The strategy's display name, e.g. "Vertical spread". Sent by the server so every surface
   *  names a strategy identically and a new kind cannot ship with no word for it. */
  label: string;
  expiry: string;
  legs: OptionLeg[];
  /** Net debit (positive) or credit (negative) across the strategy's option legs. */
  netCost: Decimal;
  maxProfit: Decimal | null;
  maxProfitUnbounded: boolean;
  maxLoss: Decimal | null;
  maxLossUnbounded: boolean;
  breakevens: Decimal[];
  payoffPoints: PayoffPoint[];
  spot: OptionSpot | null;
  spotNote: string | null;
}

// ------------------------------------------------------------ FR-G5: risk, via the sidecar

/**
 * Risk figures computed by the Python analytics service (ML-1).
 *
 * These are the one part of the API that is genuinely floating point. Everything else is a
 * `Decimal` string because it is money and must not be rounded; a volatility is a statistic
 * with no exact decimal form, and pretending otherwise would imply a precision it lacks.
 */
export interface PortfolioRisk {
  annualisedVolatility: number | null;
  valueAtRisk: number | null;
  expectedShortfall: number | null;
  maxDrawdown: number | null;
}

export interface AssetRisk {
  symbol: string;
  weight: number | null;
  annualisedVolatility: number | null;
  maxDrawdown: number | null;
  /** Share of portfolio variance. Reads above `weight` for a position carrying its weight in risk. */
  riskContribution: number | null;
}

export interface RiskCorrelation {
  a: string;
  b: string;
  correlation: number | null;
}

/**
 * `available` false always carries `reason` - no sidecar, too little history, nothing priced.
 * That is a degraded panel rather than an error, and the reason is what goes on screen.
 * `skipped` names holdings left out, so the figures are never quietly partial.
 */
export interface RiskReport {
  available: boolean;
  reason: string | null;
  from: string | null;
  to: string | null;
  observations: number | null;
  assetsAnalysed: number | null;
  confidence: number | null;
  portfolio: PortfolioRisk | null;
  assets: AssetRisk[];
  correlations: RiskCorrelation[];
  notes: string[];
  skipped: string[];
}


// ------------------------------------------------- ML-2 / ML-3: optimisers and regime

export type OptimiseMethod = 'HRP' | 'ERC' | 'REGIME' | 'CVAR';

/** One asset's current weight, the method's target, and the move between them. */
export interface WeightRow {
  symbol: string;
  current: number | null;
  target: number | null;
  delta: number | null;
}

export interface RiskProfile {
  annualisedVolatility: number | null;
  valueAtRisk: number | null;
  expectedShortfall: number | null;
  maxDrawdown: number | null;
}

/**
 * ML-3's reading of the current phase. Present only for the REGIME method, and only when the
 * fit was usable - a two-state model that found one phase reports nothing rather than a
 * coin flip.
 */
export interface RegimeReading {
  state: 'CALM' | 'STRESS';
  stressProbability: number | null;
  calmProbability: number | null;
  defensiveShare: number | null;
  calmVolatility: number | null;
  stressVolatility: number | null;
  stressShareOfWindow: number | null;
  observations: number | null;
}

/**
 * `available` false always carries `reason`. `converged` false means the optimiser is unsure of
 * its own answer, which is shown rather than hidden - an approximate target presented as a
 * target is the failure this field exists to prevent.
 */
/**
 * SRV-2. One named crisis priced against both books.
 *
 * Both deltas come from a single shock vector, so they differ because the weights do and for no
 * other reason. Negative is a loss, as a fraction of the portfolio.
 */
export interface StressComparison {
  id: string;
  name: string;
  currentDelta: number | null;
  proposedDelta: number | null;
}

/**
 * SRV-2. The option exposure an optimiser could not see.
 *
 * The optimisers price what has a close and a price history, which in this book is the equity
 * half. These are Decimal strings like every other exact figure - `delta` is shares of the
 * underlying, `theta` is money a day. `unpricedContracts` counts the contracts whose greeks the
 * feed does not carry: exposure that is missing from these totals rather than absent from the
 * book.
 */
export interface NetGreeks {
  delta: Decimal | null;
  gamma: Decimal | null;
  theta: Decimal | null;
  vega: Decimal | null;
  optionContracts: number;
  unpricedContracts: number;
  source: string | null;
}

export interface OptimiseReport {
  available: boolean;
  reason: string | null;
  method: OptimiseMethod;
  from: string | null;
  to: string | null;
  observations: number | null;
  assetsAnalysed: number | null;
  confidence: number | null;
  converged: boolean | null;
  current: RiskProfile | null;
  proposed: RiskProfile | null;
  weights: WeightRow[];
  regime: RegimeReading | null;
  beta: number | null;
  /**
   * QNT-1's own optimum: the mean loss over the worst (1 - confidence) of the window, as a
   * positive fraction. Present only for CVAR, and null - never zero - for the methods that
   * compute no tail at all.
   */
  cvar: number | null;
  stress: StressComparison | null;
  netGreeks: NetGreeks | null;
  notes: string[];
  skipped: string[];
}


// ------------------------------------------------------------- QNT-2: historical stress

export interface StressedHolding {
  symbol: string;
  /** The return this holding takes in the scenario, as a fraction. */
  shock: number | null;
  /** Its share of the portfolio move: weight times shock. */
  contribution: number | null;
}

export interface StressScenario {
  id: string;
  name: string;
  note: string;
  days: number | null;
  factorShock: number | null;
  volatilityScale: number | null;
  portfolioDelta: number | null;
  worst: StressedHolding[];
}

/**
 * All four scenarios in one answer, so selecting one costs no round trip.
 *
 * `factorShareOfVariance` is the caveat to read first: it is how much of the book's movement
 * the dominant factor explains, and when it is low these figures describe less of the
 * portfolio than they appear to.
 */
/**
 * ML-3's current phase, read on its own rather than as a side effect of proposing weights.
 *
 * Unlike `/optimise?method=REGIME`, this computes no target allocation - it answers what phase
 * the book's own returns are in right now, and nothing else.
 */
export interface RegimeReport {
  available: boolean;
  reason: string | null;
  from: string | null;
  to: string | null;
  regime: RegimeReading | null;
}

export interface StressReport {
  available: boolean;
  reason: string | null;
  from: string | null;
  to: string | null;
  observations: number | null;
  assetsAnalysed: number | null;
  factorShareOfVariance: number | null;
  averageBeta: number | null;
  computeMillis: number | null;
  /** Decimal string: money keeps its exact form even here (D-07). */
  totalValue: Decimal | null;
  currency: string | null;
  scenarios: StressScenario[];
  skipped: string[];
}


// ------------------------------------------------------------------ QNT-4: netted greeks

/**
 * One underlying's exposure, netted across every contract and share held on it.
 *
 * `delta` is the whole position in shares of the underlying. The option and share halves are
 * carried separately because the interesting cases are the ones where they pull against each
 * other - a covered call is 100 shares and a written call, and only the combined figure says
 * what the position actually is.
 *
 * Decimal strings, like every other BigDecimal on this API. Declaring them as `number` was a
 * guess about the wire rather than a reading of it: the backend serialises BigDecimal as a
 * string so an exact figure survives the round trip (D-07), and the screen crashed on
 * `toFixed is not a function` the moment it met a real one. The compiler cannot catch a type
 * that lies about what arrives.
 */
export interface GreeksByUnderlying {
  underlying: string;
  delta: Decimal | null;
  optionDelta: Decimal | null;
  shareDelta: Decimal | null;
  gamma: Decimal | null;
  theta: Decimal | null;
  vega: Decimal | null;
  contracts: number;
}

/**
 * FR-E9. One option contract's own delta, unsigned by position direction - a call's delta is
 * positive and a put's is negative regardless of who holds it. Multiply into a notional that is
 * already signed by quantity; signing this too would cancel that sign rather than combine with
 * it. A position with no priced delta is simply absent here, not present at zero.
 */
export interface PositionDelta {
  instrumentId: string;
  symbol: string;
  delta: Decimal;
}

/** `unpriced` names every contract left out, because no greeks is not the same as no exposure. */
export interface GreeksReport {
  available: boolean;
  reason: string | null;
  asOf: string | null;
  source: string | null;
  delta: Decimal | null;
  gamma: Decimal | null;
  theta: Decimal | null;
  vega: Decimal | null;
  byUnderlying: GreeksByUnderlying[];
  unpriced: string[];
  positions: PositionDelta[];
}


// ------------------------------------------------- UI-1 (EPIC 4): strategic allocation

/**
 * One asset class's plan against its reality.
 *
 * `target` is null for a class held but never planned for - drift of the more interesting kind,
 * being exposure nobody decided on. Percentages are Decimal strings like every other exact
 * figure on this API: a corridor tested with binary floats triggers on its own boundary.
 */
export interface AllocationTargetRow {
  assetClass: string;
  target: Decimal | null;
  actual: Decimal | null;
  drift: Decimal | null;
  breached: boolean;
  decidedAt: string | null;
}

/**
 * `planIncomplete` when the targets do not add to 100. Reported rather than refused: a
 * half-entered plan is a real state, and rejecting the write would lose what was typed.
 */
export interface AllocationTargetReport {
  available: boolean;
  reason: string | null;
  corridor: Decimal;
  planTotal: Decimal;
  planIncomplete: boolean;
  currency: string;
  holdingsValued: number;
  holdingsConsidered: number;
  rows: AllocationTargetRow[];
}


// ------------------------------------------------- FR-E7: trailing income and yield on cost

export interface IncomeRow {
  symbol: string;
  name: string;
  income: Decimal;
  /** Null for income from a position since sold: real income, but no current basis. */
  costBasis: Decimal | null;
  yieldOnCost: Decimal | null;
}

/**
 * Trailing twelve months of income, and what it yields on what was paid.
 *
 * Yield on cost rather than yield: dividends over the purchase price, not today's. The two
 * diverge exactly when a holding has run, and it is the first that says whether the original
 * decision is still earning.
 */
export interface IncomeReport {
  available: boolean;
  reason: string | null;
  from: string | null;
  to: string | null;
  currency: string;
  dividends: Decimal;
  interest: Decimal;
  staking: Decimal;
  rental: Decimal;
  total: Decimal;
  costBasis: Decimal | null;
  yieldOnCost: Decimal | null;
  dividendYieldOnCost: Decimal | null;
  holdingsCovered: number;
  holdingsTotal: number;
  /** Income in a currency other than the account's base, deliberately outside the totals. */
  otherCurrencies: string[];
  rows: IncomeRow[];
}


// ------------------------------------------------------- AST-2: liabilities and net worth

export type LiabilityKind =
  | 'MORTGAGE'
  | 'INSTALMENT'
  | 'LOAN'
  | 'CREDIT_LINE'
  | 'MARGIN'
  | 'OTHER';

/** A stored debt. `settled` marks one paid down to zero and kept for history. */
/**
 * What a new or corrected liability carries.
 *
 * <p>Only `kind`, `name` and `principalOutstanding` are required. The rest are context the
 * balance does not need but a reader does - a rate and a maturity say whether a debt is
 * expensive and how long it lasts, and nothing derives them.
 */
export interface LiabilityRequest {
  kind: LiabilityKind;
  name: string;
  principalOutstanding: Decimal;
  /** The date the balance was true. Defaults to today server-side when omitted; a liability is
   *  a mark carried forward, never a live figure. */
  outstandingAsOf?: string | null;
  currency?: string | null;
  securedOnInstrumentId?: string | null;
  originalPrincipal?: Decimal | null;
  annualRatePercent?: Decimal | null;
  monthlyPayment?: Decimal | null;
  openedOn?: string | null;
  maturesOn?: string | null;
  notes?: string | null;
}

export interface Liability {
  id: string;
  kind: LiabilityKind;
  name: string;
  principalOutstanding: Decimal;
  currency: string;
  /** The date the outstanding balance was last true. Always shown beside the figure: the
   *  balance is a mark carried forward, not a live number. */
  outstandingAsOf: string;
  originalPrincipal: Decimal | null;
  annualRatePercent: Decimal | null;
  monthlyPayment: Decimal | null;
  openedOn: string | null;
  maturesOn: string | null;
  securedOnInstrumentId: string | null;
  notes: string | null;
  settled: boolean;
}

/** A liability as it appears inside a net-worth report. `amountInBase` is null when no FX rate
 *  could be resolved - the row is still listed, because a debt that cannot be counted is
 *  precisely the one someone needs to see. */
export interface NetWorthLiability extends Liability {
  amountInBase: Decimal | null;
  baseCurrency: string;
}

/** One asset and the debt secured against it. `equity` is null (never zero) when the asset
 *  itself could not be valued: "unknown" and "none" are opposite claims. */
export interface SecuredAsset {
  symbol: string;
  name: string;
  baseCurrency: string;
  assetValue: Decimal | null;
  securedDebt: Decimal;
  equity: Decimal | null;
  underwater: boolean;
}

/**
 * AST-2. What is owned minus what is owed.
 *
 * The two ways this can be incomplete are not equally safe. An unpriced holding drops off the
 * asset side and *understates* net worth - conservative, and visible as a smaller number. An
 * unconvertible debt drops off the liability side and *overstates* it - a flattering error, and
 * the one that gets believed. `complete` is false for either; `reason` says which, and in which
 * direction the total is consequently wrong. Render the reason whenever `complete` is false.
 */
/** One currency's cash balance. `inBase` is null when no FX rate could be resolved; the row
 *  stays visible so an excluded balance cannot be mistaken for an absent one. */
export interface CashBalanceView {
  currency: string;
  balance: Decimal;
  inBase: Decimal | null;
  asOf: string;
}

export interface NetWorthReport {
  currency: string;
  totalAssets: Decimal;
  holdingsValued: number;
  holdingsTotal: number;
  /** A11. The broker's reported cash, in the account's base currency. Negative is a margin loan.
   *  Null when no statement has been imported - not zero, which would assert that the account
   *  holds no cash, and would flatter exactly the accounts where it is most wrong. */
  cash: Decimal | null;
  /** The date the cash balance was last true: a statement's period end, not today. */
  cashAsOf: string | null;
  cashByCurrency: CashBalanceView[];
  totalLiabilities: Decimal;
  liabilitiesConverted: number;
  liabilitiesTotal: number;
  netWorth: Decimal;
  /** Debt as a percentage of assets. Null when there are no assets to be a share of - never
   *  zero, which would read as "no debt". */
  debtRatio: Decimal | null;
  equityRatio: Decimal | null;
  complete: boolean;
  reason: string | null;
  unconvertible: string[];
  liabilities: NetWorthLiability[];
  securedAssets: SecuredAsset[];
}


// -------------------------------------------------------------------------- INT-3: track record

/** One instrument's target weight within a run. */
export interface OptimiserRunWeight {
  symbol: string;
  targetWeight: number;
}

/**
 * One horizon's check against what actually happened, or the reason it could not be done.
 * `available` false always carries `reason`. `skippedSymbols` can be non-empty even when
 * `available` is true - a figure computed over 80% of the proposed weight is real, but it is
 * not the same claim as one over all of it.
 */
export interface OptimiserRunEvaluation {
  horizonDays: number;
  evaluatedAt: string;
  available: boolean;
  reason: string | null;
  skippedSymbols: string[];
  observations: number | null;
  realisedReturn: number | null;
  realisedVolatility: number | null;
  realisedDrawdown: number | null;
}

/**
 * INT-3. One optimiser run, what it proposed, and every evaluation of it that exists so far -
 * none, some, or all three of 30/90/180 days, depending on how long ago it ran. This is the
 * only thing on the roadmap that can ever say whether running the optimiser was worth it: every
 * other analytics figure only ever looks forward.
 */
export interface OptimiserRun {
  id: string;
  method: OptimiseMethod;
  runAt: string;
  windowFrom: string;
  windowTo: string;
  observations: number;
  currency: string;
  converged: boolean | null;
  proposedVolatility: number | null;
  proposedVar: number | null;
  proposedEs: number | null;
  proposedDrawdown: number | null;
  weights: OptimiserRunWeight[];
  evaluations: OptimiserRunEvaluation[];
}

// ---------------------------------------------------------------- audit wizard

export type WizardRiskTolerance = 'CONSERVATIVE' | 'BALANCED' | 'AGGRESSIVE';
export type WizardLiquidityNeed = 'HIGH' | 'MODERATE' | 'LOW';
export type InsightSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

/** Step 1's answers: the mandate every later finding is scored against. */
export interface WizardProfile {
  riskTolerance: WizardRiskTolerance | null;
  liquidityNeed: WizardLiquidityNeed | null;
  horizonYears: number | null;
  emergencyFundMonths: number | null;
  /** Living costs outside debt service. Null means the reader would rather not say, and the
   *  coverage card then measures debt payments alone and states that it did. */
  monthlyExpenses: Decimal | null;
  /** What `monthlyExpenses` is denominated in; null means the account's base currency. Asked
   *  for because the figure is added to debt service, which is already in base - a number typed
   *  in one currency and summed as another is wrong by the exchange rate and looks ordinary. */
  monthlyExpensesCurrency: string | null;
  objective: string | null;
}

/**
 * A figure and what it is.
 *
 * `value` is null when the figure could not be computed. Render that as a gap naming `note` -
 * never as a dash or a zero, which read as "measured, and it is fine".
 */
export interface InsightMetric {
  label: string;
  value: string | null;
  note: string | null;
}

/** One audit card: a written conclusion and the figures behind it. Formatted server-side, so the
 *  sentence and the number it quotes cannot round differently. */
export interface Insight {
  step: number;
  code: string;
  title: string;
  severity: InsightSeverity;
  keyMetrics: InsightMetric[];
  bullets: string[];
  recommendation: string | null;
}

/** Step 3's answers: the reader's own crisis, on top of the historical episodes. */
export interface WizardStress {
  /** How far the broad market falls, as a positive percentage — 30 means "the market falls 30%".
   *  Positive because that is how the question reads out loud; the sign is applied server-side,
   *  in one place, rather than left to whoever types it. */
  customShockPercent: Decimal | null;
}

export type WizardMethod = 'HRP' | 'ERC' | 'CVAR' | 'REGIME' | 'BL';

/** One Black-Litterman opinion. Meaningful only for BL; every other method needs no
 *  expected-return estimate at all, which is why they are the safer default. */
export interface WizardViewInput {
  symbol: string;
  /** Annualised, as a percentage: 8 means eight percent. */
  expectedReturnPercent: Decimal | null;
}

/** Step 4's answers: which optimiser to ask, and what the reader believes. */
export interface WizardTarget {
  method: WizardMethod | null;
  views: WizardViewInput[] | null;
  /** The LDI floor, as an annual percentage the target must clear. Null for no floor. */
  incomeFloorPercent: Decimal | null;
}

/** Step 5's answers: the one thing the transition plan cannot work out for itself. */
export interface WizardTransition {
  /** The rate applied to net realised gains, or null. Null means the cards report the gain and
   *  loss and say plainly that the tax was not computed - assuming a rate and printing a number
   *  would be a confident wrong answer about money owed to a government. */
  capitalGainsRatePercent: Decimal | null;
}

export interface WizardView {
  id: string;
  accountId: string;
  /** The step these cards are for. */
  step: number;
  /** The furthest step answered, which is what the stepper enables. */
  currentStep: number;
  lastStep: number;
  completed: boolean;
  profile: WizardProfile | null;
  stress: WizardStress | null;
  target: WizardTarget | null;
  transition: WizardTransition | null;
  insights: Insight[];
  createdAt: string;
  updatedAt: string;
}

export interface WizardStepRequest {
  step: number;
  /** Required on step 1, ignored elsewhere. */
  profile?: WizardProfile | null;
  /** Optional on step 3, ignored elsewhere. */
  stress?: WizardStress | null;
  /** Optional on step 4, ignored elsewhere. */
  target?: WizardTarget | null;
  /** Optional on step 5, ignored elsewhere. */
  transition?: WizardTransition | null;
}

export interface WizardSummary {
  id: string;
  startedAt: string;
  committedAt: string;
  stepsReached: number;
}

// ---------------------------------------------------------------------------------- FR-K briefing

/** FR-K1. The limits a briefing is measured against. Percentages are of net liquidation value. */
export interface BriefingLimits {
  cashMinPct: Decimal;
  cashTargetPct: Decimal;
  maxLeverage: Decimal;
  maxSingleNamePct: Decimal;
  maxThemePct: Decimal;
  speculativeBeta: Decimal;
  maxSpeculativePct: Decimal;
  maxEventLossPct: Decimal;
  lossReviewPct: Decimal;
  urgentDte: number;
}

export interface BriefingMetrics {
  nav: Decimal | null;
  cash: Decimal | null;
  cashPct: Decimal | null;
  netDeltaDollars: Decimal | null;
  grossLongDeltaDollars: Decimal | null;
  betaWeightedDollars: Decimal | null;
  /** Beta-weighted delta dollars over net liquidation value. */
  leverage: Decimal | null;
  largestName: string | null;
  largestNamePct: Decimal | null;
  largestTheme: string | null;
  largestThemePct: Decimal | null;
  thetaPerDay: Decimal | null;
  navNote: string | null;
}

export interface BriefingExposureRow {
  underlying: string;
  shares: Decimal;
  optionDeltaShares: Decimal;
  deltaShares: Decimal;
  spot: Decimal | null;
  /** Null when the underlying has no price; `note` says why. Never a zero standing in. */
  deltaDollars: Decimal | null;
  pctNav: Decimal | null;
  beta: Decimal;
  betaAssumed: boolean;
  betaWeightedDollars: Decimal | null;
  theme: string | null;
  /** The provider's industry (D-17). Shown beside a missing theme, never counted as one. */
  sector: string | null;
  contractsWithoutGreeks: string[];
  note: string | null;
}

export interface BriefingThemeRow {
  /** Null is the group of symbols with no theme yet. */
  theme: string | null;
  deltaDollars: Decimal;
  pctNav: Decimal | null;
  members: string[];
}

export interface BriefingLeg {
  symbol: string;
  quantity: Decimal;
  strike: Decimal;
  right: 'CALL' | 'PUT';
  expiry: string;
  costBasis: Decimal;
}

export interface BriefingShortLeg {
  symbol: string;
  expiry: string;
  dte: number;
  strike: Decimal;
  right: 'CALL' | 'PUT';
  itm: boolean | null;
  otmPct: Decimal | null;
}

export interface BriefingStructure {
  underlying: string;
  /** A strategy kind, or CALENDAR / DIAGONAL / PMCC for the cross-expiry structures. */
  kind: string;
  label: string;
  firstExpiry: string;
  lastExpiry: string;
  legs: BriefingLeg[];
  netCost: Decimal;
  marketValue: Decimal | null;
  pnl: Decimal | null;
  maxProfit: Decimal | null;
  maxProfitUnbounded: boolean;
  maxLoss: Decimal | null;
  maxLossUnbounded: boolean;
  breakevens: Decimal[];
  /** False for cross-expiry structures, whose maxima need option prices and are left null. */
  payoffExact: boolean;
  stockBacked: boolean;
  shortLegs: BriefingShortLeg[];
  pmccWidth: Decimal | null;
  spot: Decimal | null;
}

export interface BriefingStockRow {
  symbol: string;
  quantity: Decimal;
  costBasis: Decimal;
  marketValue: Decimal | null;
  pnl: Decimal | null;
  pnlPct: Decimal | null;
  theme: string | null;
  sector: string | null;
}

export type BriefingSeverity = 'critical' | 'serious' | 'warning' | 'info';

/** A finding as data; the page phrases it. */
export interface BriefingFlag {
  severity: BriefingSeverity;
  code: string;
  subject: string | null;
  value: Decimal | null;
  limit: Decimal | null;
  detail: string | null;
}

export interface BriefingHealth {
  cash: number;
  leverage: number;
  singleName: number;
  theme: number;
  definedRisk: number;
  total: number;
}

export interface Briefing {
  asOf: string;
  currency: string;
  limits: BriefingLimits;
  metrics: BriefingMetrics;
  health: BriefingHealth;
  flags: BriefingFlag[];
  exposure: BriefingExposureRow[];
  themes: BriefingThemeRow[];
  structures: BriefingStructure[];
  stocks: BriefingStockRow[];
  /** FR-K6. Events and written-leg expiries from today on, in date order. */
  calendar: BriefingCalendarRow[];
  /** Everything the figures could not include, and why. */
  gaps: string[];
}

export type MarketEventType =
  | 'EARNINGS'
  | 'EX_DIVIDEND'
  | 'INVESTOR_DAY'
  | 'REGULATORY'
  | 'MACRO'
  | 'OTHER';

/** FR-K6. One line of the calendar: a dated event, or the expiry of a written option. */
export interface BriefingCalendarRow {
  date: string;
  /** The underlying, or MACRO for market-wide dates. */
  symbol: string;
  kind: 'EVENT' | 'EXPIRY';
  type?: MarketEventType;
  /** bmo / amc / dmh, when the source says. */
  hour?: string;
  confirmed?: boolean;
  source?: 'finnhub' | 'manual' | 'skill';
  /** For an expiry, the contract written. */
  note?: string;
  eventId?: string;
  /** For an expiry, "UNDERLYING KIND" of the structure it belongs to. */
  structure?: string;
}

export interface MarketEvent {
  id: string;
  symbol: string;
  date: string;
  type: MarketEventType;
  hour: string | null;
  confirmed: boolean;
  source: 'finnhub' | 'manual' | 'skill';
  note: string | null;
}

export interface MarketEventRequest {
  symbol: string;
  date: string;
  type: MarketEventType;
  hour?: string | null;
  confirmed?: boolean;
  note?: string | null;
}

export interface ScenarioContribution {
  underlying: string;
  pnl: Decimal;
}

/** FR-K7. An instant index move, today, with volatility reacting to it. */
export interface ScenarioStress {
  indexMovePct: Decimal;
  vix: Decimal;
  pnl: Decimal;
  pctNav: Decimal;
  /** Index and volatility positions together. */
  hedgesPnl: Decimal;
  byUnderlying: ScenarioContribution[];
}

/** FR-K7. One case on the horizon: an index move, a VIX level, a probability. */
export interface ScenarioOutcome {
  name: string;
  probability: Decimal;
  indexMovePct: Decimal;
  vix: Decimal;
  nav: Decimal;
  pnl: Decimal;
  pctNav: Decimal;
  hedgesPnl: Decimal;
  byUnderlying: ScenarioContribution[];
}

export interface ScenarioResult {
  asOf: string;
  horizonDate: string;
  vix: Decimal;
  stress: ScenarioStress[];
  scenarios: ScenarioOutcome[];
  expectedPnl: Decimal;
  expectedPct: Decimal;
  /** What the model had to assume: a volatility, a VIX level, an unpriced underlying. */
  notes: string[];
}

export interface ScenarioReport {
  available: boolean;
  /** Present exactly when unavailable. */
  reason?: string;
  result?: ScenarioResult;
  /** Positions the request could not carry, with the reason. */
  leftOut: string[];
}

/** D-17. What asking Finnhub for the account's report dates did. */
export interface EventSyncOutcome {
  /** False when the server has no Finnhub key; nothing was asked. */
  configured: boolean;
  symbols: number;
  written: number;
  /** Reports skipped because a date from another source already stands. */
  kept: string[];
  failed: string[];
}

export interface BriefingReport {
  briefing: Briefing;
  benchmark: string;
  /** Option underlyings with no stock record here, so no price and no beta. */
  untrackedUnderlyings: string[];
}

/** D-17. What filling empty sectors from Finnhub did. */
export interface SectorFillOutcome {
  /** False when the server has no Finnhub key; nothing was asked. */
  configured: boolean;
  filled: string[];
  notCovered: string[];
  failed: string[];
}

export interface TrackedUnderlying {
  symbol: string;
  bars: number | null;
  error: string | null;
}
